import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { useDispatch, useSelector } from "react-redux";
import { fetchSapWorklist, syncWorklistCurrentDay } from "../../../core/data/redux/sapWorklistSlice";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import { isMedicalAttendanceWorklistItem } from "../otherServices/medicalAttendance/medicalAttendance.service";
import { isLeaveEncashmentWorklistItem } from "../otherServices/leaveEncashment/leaveEncashment.service";
import { isHallBookingWorklistItem } from "../otherServices/communityHall/communityHall.service";
import { isStaffWelfareWorklistItem } from "../otherServices/staffWelfareFund/staffWelfare.service";
import { isEAttestationWorklistItem } from "../eAttestation/eAttestation.service";
import { getSv4ApprovalPath, isSv4WorklistItem } from "../sv4Card/sv4Card.service";
import { detectInclusionKind } from "../dependentFamily/helpers";
import TasksTab from "./TasksTab";
import AlertsTab from "./AlertsTab";
import NotificationsTab from "./NotificationsTab";
import MailsTab from "./MailsTab";
import TrackingTab from "./TrackingTab";
import Toasts from "../../../core/common/toast/index";
import { replaceUserId } from "./nocApprovals/common/nocUtils";
import "./UniversalWorklist.scss";

interface UniversalWorklistProps { }

interface WorkItem {
  id: number;
  subject: string;
  from: string;
  sentDate: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'New' | 'In Progress' | 'Completed';
  dueDate?: string;
  type: 'task' | 'alert' | 'notification' | 'mail' | 'tracking';
}

const UniversalWorklist = (props: UniversalWorklistProps) => {
  const routes = all_routes;
  const [activeTab, setActiveTab] = useState<'tasks' | 'alerts' | 'notifications' | 'mails' | 'tracking'>('tasks');

  // Sample data - replace with actual API data
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const employeesState = useSelector((state: any) => state.employeesState);
  const empId = employeesState?.data?.data?.empId || "";

  const sapWorklistState = useSelector((state: any) => state.sapWorklistState);
  const { worklistData, loading, syncLoading } = sapWorklistState;

  // Toast state
  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
    type: "success" as "success" | "danger" | "warning" | "primary",
  });

  const showToast = (
    title: string,
    message: string,
    type: "success" | "danger" | "warning" | "primary"
  ) => {
    setToast({
      show: true,
      title,
      message,
      type,
    });
    setTimeout(() => {
      setToast((prev) => ({ ...prev, show: false }));
    }, 3000);
  };

  const handleSync = async () => {
    try {
      await dispatch(syncWorklistCurrentDay() as any).unwrap();
      showToast("Success", "Sync triggered successfully", "success");
      dispatch(fetchSapWorklist({ page: "1", limit: "50" }) as any);
    } catch (error: any) {
      showToast("Error", error?.message || "Something went wrong", "danger");
    }
  };

  useEffect(() => {
    if (!empId) {
      dispatch(getMyProfile({}) as any);
    }
  }, [dispatch, empId]);

  useEffect(() => {
    dispatch(fetchSapWorklist({ page: "1", limit: "50" }) as any);
  }, [dispatch]);

  const workItems: WorkItem[] = (worklistData?.data || []).map((item: any) => ({
    id: item.id,
    subject: replaceUserId(item.workItemText || item.taskText || "No Subject", item.empId || item.creator),
    from: item.creator || item.empId || "System",
    sentDate: item.creationDate ? `${item.creationDate} ${item.creationTime || ""}` : "Recently",
    priority: "Medium",
    status: item.status === "READY" ? "New" : "In Progress",
    type: 'task',
    originalItem: item
  }));

  const handleItemSelect = (item: any) => {
    const subject = (item.subject || "").toLowerCase();
    const requestId = item.originalItem?.requestID || item.id.toString();
    const original = item.originalItem || {};

    if (isStaffWelfareWorklistItem(original) || subject.includes('staff welfare') || subject.includes('swf')) {
      navigate(routes.todoWorklist.staffWelfareApproval.replace(':requestID', encodeURIComponent(requestId)));
    } else if (isEAttestationWorklistItem(original) || subject.includes('e-attestation') || subject.includes('e attestation') || subject.includes('attestation form')) {
      navigate(
        routes.todoWorklist.eAttestationApproval.replace(
          ':requestID',
          encodeURIComponent(String(original.requestID || original.id || item.id))
        )
      );
    } else if (isSv4WorklistItem(original) || isSv4WorklistItem({ subject, type: original.type })) {
      navigate(getSv4ApprovalPath({ requestID: requestId, id: original.id || item.id }));
    } else if (isMedicalAttendanceWorklistItem(original) || subject.includes('ivf') || subject.includes('para 6.2') || subject.includes('para 6.3')) {
      navigate(routes.todoWorklist.medicalAttendanceApproval.replace(':requestID', encodeURIComponent(requestId)));
    } else if (isHallBookingWorklistItem(original) || subject.includes('community hall') || subject.includes('hall booking')) {
      navigate(routes.todoWorklist.hallBookingApproval.replace(':requestID', encodeURIComponent(requestId)));
    } else     if (isLeaveEncashmentWorklistItem(original) || subject.includes('leave encash')) {
      navigate(routes.todoWorklist.leaveEncashmentApproval.replace(':requestID', encodeURIComponent(requestId)));
    } else if (detectInclusionKind({ ...original, subject })) {
      const kind = detectInclusionKind({ ...original, subject });
      const path =
        kind === "delete"
          ? routes.todoWorklist.inclusionDeletionApproval
          : kind === "family"
            ? routes.todoWorklist.inclusionFamilyApproval
            : routes.todoWorklist.inclusionParentsApproval;
      navigate(path.replace(":requestID", encodeURIComponent(requestId)));
    } else if (subject.includes('leave')) {
      navigate(routes.todoWorklist.leaveApprovalPage.replace(':id', requestId));
    } else if (subject.includes('i-card') || subject.includes('id-card') || subject.includes('identity card')) {
      navigate(routes.todoWorklist.idCardApprovalDetail.replace(':id', requestId));
    } else if (subject.includes('noc')) {
      navigate(routes.todoWorklist.nocApprovalDetail.replace(':id', item.id.toString()));
    } else if (subject.includes('vpf') || item.originalItem?.type?.includes('VPF')) {
      navigate(routes.todoWorklist.taskDetails.replace(':id', requestId) + `?type=${item.originalItem?.type || 'VPF_APPLICATION'}`);
    } else {
      navigate(routes.todoWorklist.taskDetails.replace(':id', item.id.toString()) + `?type=${item.originalItem?.type || ''}`);
    }
  };

  const getFilteredItems = () => {
    switch (activeTab) {
      case 'tasks':
        return workItems.filter(item => item.type === 'task');
      case 'alerts':
        return workItems.filter(item => item.type === 'alert');
      case 'notifications':
        return workItems.filter(item => item.type === 'notification');
      case 'mails':
        return workItems.filter(item => item.type === 'mail');
      case 'tracking':
        return workItems.filter(item => item.type === 'tracking');
      default:
        return workItems.filter(item => item.type === 'task');
    }
  };

  const getTabCount = (type: string) => {
    return workItems.filter(item => item.type === type).length;
  };

  return (
    <div className="page-wrapper universal-worklist">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Universal Worklist</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Universal Worklist
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="me-2 mb-2">
              <div className="dropdown">
                <Link
                  to="#"
                  className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-filter me-2"></i>
                  Filter
                </Link>
                <ul className="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <Link to="#" className="dropdown-item rounded-1">
                      All Items
                    </Link>
                  </li>
                  <li>
                    <Link to="#" className="dropdown-item rounded-1">
                      High Priority
                    </Link>
                  </li>
                  <li>
                    <Link to="#" className="dropdown-item rounded-1">
                      Due Today
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
            <div className="me-2 mb-2">
              <button
                className="btn btn-outline-primary d-flex align-items-center"
                onClick={handleSync}
                disabled={syncLoading}
              >
                <i className="ti ti-reload me-2"></i>
                {syncLoading ? "Syncing..." : "Sync"}
              </button>
            </div>
            <div className="me-2 mb-2">
              <Link
                to="#"
                className="btn btn-primary d-flex align-items-center"
                onClick={() => dispatch(fetchSapWorklist({ page: "1", limit: "50" }) as any)}
              >
                <i className="ti ti-refresh me-2"></i>
                Refresh
              </Link>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body p-0">
                {/* Tab Navigation */}
                <div className="border-bottom tab-navigation">
                  <nav className="d-flex">
                    <button
                      className={`nav-link border-0 bg-transparent px-3 py-3 fw-medium ${activeTab === 'tasks' ? 'active text-primary' : 'text-muted'
                        }`}
                      onClick={() => setActiveTab('tasks')}
                    >
                      Tasks ({getTabCount('task')})
                    </button>
                    <button
                      className={`nav-link border-0 bg-transparent px-3 py-3 fw-medium ${activeTab === 'alerts' ? 'active text-primary' : 'text-muted'
                        }`}
                      onClick={() => setActiveTab('alerts')}
                    >
                      Alerts ({getTabCount('alert')})
                    </button>
                    <button
                      className={`nav-link border-0 bg-transparent px-3 py-3 fw-medium ${activeTab === 'notifications' ? 'active text-primary' : 'text-muted'
                        }`}
                      onClick={() => setActiveTab('notifications')}
                    >
                      Notifications ({getTabCount('notification')})
                    </button>
                    <button
                      className={`nav-link border-0 bg-transparent px-3 py-3 fw-medium ${activeTab === 'mails' ? 'active text-primary' : 'text-muted'
                        }`}
                      onClick={() => setActiveTab('mails')}
                    >
                      Office Mails ({getTabCount('mail')})
                    </button>
                    <button
                      className={`nav-link border-0 bg-transparent px-3 py-3 fw-medium ${activeTab === 'tracking' ? 'active text-primary' : 'text-muted'
                        }`}
                      onClick={() => setActiveTab('tracking')}
                    >
                      Tracking ({getTabCount('tracking')})
                    </button>
                  </nav>
                </div>

                {/* Filter Bar */}
                <div className="p-3 bg-light border-bottom filter-bar">
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <div className="d-flex align-items-center">
                      <span className="me-3">Show:</span>
                      <select className="form-select form-select-sm me-3" style={{ width: 'auto' }}>
                        <option value="new-in-progress">New and In Progress Items ({getFilteredItems().length} / {getFilteredItems().length})</option>
                        <option value="all">All Items</option>
                        <option value="completed">Completed Items</option>
                      </select>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button className="btn btn-sm btn-outline-secondary" title="Refresh">
                        <i className="ti ti-refresh"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-secondary" title="Print">
                        <i className="ti ti-printer"></i>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="tab-content">
                  {activeTab === 'tasks' && (
                    <TasksTab
                      tasks={getFilteredItems()}
                      onItemSelect={handleItemSelect}
                    />
                  )}
                  {activeTab === 'alerts' && (
                    <AlertsTab
                      alerts={getFilteredItems()}
                      onItemSelect={handleItemSelect}
                    />
                  )}
                  {activeTab === 'notifications' && (
                    <NotificationsTab
                      notifications={getFilteredItems()}
                      onItemSelect={handleItemSelect}
                    />
                  )}
                  {activeTab === 'mails' && (
                    <MailsTab
                      mails={getFilteredItems()}
                      onItemSelect={handleItemSelect}
                    />
                  )}
                  {activeTab === 'tracking' && (
                    <TrackingTab
                      trackingItems={getFilteredItems()}
                      onItemSelect={handleItemSelect}
                    />
                  )}
                </div>

                {/* Pagination */}
                <div className="d-flex align-items-center justify-content-between p-3 border-top">
                  <div className="d-flex align-items-center">
                    <span className="text-muted">
                      Showing {getFilteredItems().length} of {getFilteredItems().length} entries
                    </span>
                  </div>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className="page-item disabled">
                        <span className="page-link">Previous</span>
                      </li>
                      <li className="page-item active">
                        <span className="page-link">1</span>
                      </li>
                      <li className="page-item disabled">
                        <span className="page-link">Next</span>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toasts
        show={toast.show}
        title={toast.title}
        message={toast.message}
        type={toast.type}
      />
    </div>
  );
};

export default UniversalWorklist;